<script setup lang="ts">
import { reactivePick } from '@vueuse/core';
import { createReusableTemplate } from '@vueuse/core';
import {
  ToastAction,
  ToastClose,
  ToastDescription,
  ToastRoot,
  ToastTitle,
  useForwardPropsEmits,
} from 'reka-ui';
import { type Component, computed, nextTick, onMounted, ref } from 'vue';

import { KaButton } from '@/kasten-components/components/KaButton';
import { KaProgress } from '@/kasten-components/components/KaProgress';
import { useDataTestid } from '@/kasten-components/composables';
import { FALLBACK_TOAST_HEIGHT } from '@/kasten-components/utils';
import { blurActiveElement } from '@/utils/focus-utils';
import AlertCircleIcon from '~icons/ion/alert-circle-outline';
import CheckCircleIcon from '~icons/ion/checkmark-circle-outline';
import XMarkIcon from '~icons/ion/close-outline';
import InformationCircleIcon from '~icons/ion/information-circle-outline';
import ExclamationTriangleIcon from '~icons/ion/warning-outline';

import {
  type KaToastColor,
  toastActionsVariants,
  toastCloseVariants,
  toastContentVariants,
  toastDescriptionVariants,
  toastIconVariants,
  toastProgressVariants,
  toastTitleVariants,
  toastVariants,
} from './toast-variants';
import type { KaToastEmits, KaToastProps, KaToastSlots } from './types';
export type { KaToastAction, KaToastEmits, KaToastProps, KaToastSlots } from './types';

defineOptions({ name: 'KaToast' });

const props = withDefaults(defineProps<KaToastProps>(), {
  color: 'primary',
  orientation: 'vertical',
  showIcon: true, // Show icons by default
  iconSize: 'md',
  closable: true,
  showProgressBar: true,
  duration: 5000,
  open: true,
  type: 'background',
  position: 'top-center',
});

const emits = defineEmits<KaToastEmits>();
const slots = defineSlots<KaToastSlots>();

const { dataTestid } = useDataTestid();

// Forward props to ToastRoot
const rootProps = useForwardPropsEmits(reactivePick(props, 'open', 'duration', 'type'), emits);

// Use existing variants instead of inline theme
const ui = computed(() => ({
  root: toastVariants({
    position: props.position,
    orientation: props.orientation,
    title: !!props.title || !!slots.title,
  }),
  wrapper: toastContentVariants(),
  icon: toastIconVariants({ color: props.color, size: props.iconSize }),
  title: toastTitleVariants(),
  description: toastDescriptionVariants(),
  actions: toastActionsVariants({ orientation: props.orientation }),
  close: toastCloseVariants(),
  progress: toastProgressVariants(),
}));

// Height ref for measurements - matching Nuxt UI pattern
const el = ref();
const height = ref(0);
const frozenProgressValue = ref<number | null>(null);

// Function to handle progress value with freezing
function progressValue(remaining: number, duration: number, isOpen: boolean): number {
  // Use frozen value when closing
  if (frozenProgressValue.value) {
    return frozenProgressValue.value;
  }

  const value = (remaining / duration) * 100;

  // Freeze the value when toast is closing
  if (!isOpen) {
    frozenProgressValue.value = value;
  }

  return value;
}

const colorIconMap: Record<KaToastColor, Component> = {
  success: CheckCircleIcon,
  error: AlertCircleIcon,
  warning: ExclamationTriangleIcon,
  neutral: InformationCircleIcon,
  primary: InformationCircleIcon,
};

const defaultIcon = computed(() => colorIconMap[props.color] || InformationCircleIcon);

function handleClose() {
  // Remove focus from the close button to prevent focus issues with next toast
  blurActiveElement();
  emits('close', props.id);
}

// Height measurement for smooth animations
onMounted(() => {
  if (!el.value) {
    return;
  }

  nextTick(() => {
    // Safely get the DOM element and its height
    const domElement = el.value?.$el;
    if (domElement && typeof domElement.getBoundingClientRect === 'function') {
      height.value = domElement.getBoundingClientRect()?.height || FALLBACK_TOAST_HEIGHT;
    } else {
      height.value = FALLBACK_TOAST_HEIGHT;
    }
  });
});

// Expose height for Toaster component - matching Nuxt UI's defineExpose
defineExpose({
  height,
});

const [DefineActionButtonsTemplate, ReuseActionButtonsTemplate] = createReusableTemplate();
</script>

<template>
  <ToastRoot
    ref="el"
    v-slot="{ remaining, duration: toastDuration, open: isOpen }"
    v-bind="rootProps"
    :data-orientation="props.orientation"
    :class="ui.root"
    :data-testid="dataTestid"
    :style="{ '--height': height }"
  >
    <!-- Reusable template for action buttons -->
    <DefineActionButtonsTemplate>
      <ToastAction
        v-for="(action, index) in props.actions"
        :key="index"
        :alt-text="action.label"
        as-child
        @click.stop
      >
        <KaButton size="small" v-bind="action" @click="action.onClick">
          {{ action.label }}
        </KaButton>
      </ToastAction>
    </DefineActionButtonsTemplate>
    <!-- Leading icon slot -->
    <slot name="leading">
      <div v-if="props.showIcon" :class="ui.icon">
        <component :is="defaultIcon" class="h-full w-full" />
      </div>
    </slot>

    <!-- Wrapper for content -->
    <div :class="ui.wrapper">
      <!-- Title -->
      <ToastTitle v-if="props.title || slots.title" :class="ui.title">
        <slot name="title">
          {{ props.title }}
        </slot>
      </ToastTitle>

      <!-- Description -->
      <ToastDescription v-if="props.description || slots.description" :class="ui.description">
        <slot name="description">
          {{ props.description }}
        </slot>
      </ToastDescription>

      <!-- Actions (for vertical layout) -->
      <div
        v-if="props.orientation === 'vertical' && (props.actions?.length || slots.actions)"
        :class="ui.actions"
      >
        <slot name="actions">
          <ReuseActionButtonsTemplate />
        </slot>
      </div>
    </div>

    <!-- Actions (for horizontal layout) -->
    <div
      v-if="props.orientation === 'horizontal' && (props.actions?.length || slots.actions)"
      :class="ui.actions"
    >
      <slot name="actions">
        <ReuseActionButtonsTemplate />
      </slot>
    </div>

    <!-- Close button -->
    <ToastClose v-if="props.closable" as-child>
      <slot name="close">
        <KaButton
          size="small"
          variant="default"
          :class="ui.close"
          aria-label="Close"
          @click.stop="handleClose"
        >
          <XMarkIcon class="h-4 w-4" />
        </KaButton>
      </slot>
    </ToastClose>

    <!-- Progress bar -->
    <KaProgress
      v-if="props.showProgressBar && toastDuration"
      :value="progressValue(remaining, toastDuration, isOpen)"
      :color="props.color"
      size="sm"
      :class="ui.progress"
    />
  </ToastRoot>
</template>
